<template>
	<div>
		<base-header class="pb-6 bg-light">
			<b-row align-v="center" class="py-4">
				<b-col cols="12" md="7" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">OTA Profile Info</h6>
					&nbsp;
					<span v-if="isEditor || isAdministrator">
						<base-button type="primary" size="sm" :disabled="iccid.length === 0" v-on:click="clickActivate"
							>Activate</base-button
						>
					</span>
					&nbsp;
					<span v-if="isEditor || isAdministrator">
						<base-button type="danger" size="sm" :disabled="iccid.length === 0" v-on:click="$bvModal.show('confirm-reset')"
							>Reset</base-button
						>
					</span>
				</b-col>
				<b-col cols="12" md="5" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>

			<div v-if="noAccess">
				<b-card>
					<b-card-header>
						<p class="h2 mb-2">
							<b-icon-exclamation-triangle-fill variant="danger"></b-icon-exclamation-triangle-fill
							>&nbsp;Restricted
						</p>
					</b-card-header>
					<b-card-body>Access is Restricted</b-card-body>
				</b-card>
			</div>

			<div v-else>
				<b-row class="mb-4">
					<b-col xl="6">
						<b-input-group size="lg">
							<template #prepend>
								<b-button class="iccid-width ps" style="cursor: default" variant="primary"
									>ICCID</b-button
								>
							</template>
							<b-form-input 
                id="iccid" 
                data-cy="ota-profile-search-input" 
                type="text" 
                v-model="iccid"
                :state="!searchDisabled"
              >
            </b-form-input>
            <b-input-group-append>
              <b-button
              v-if="showRefresh"
              data-cy="ota-profile-refresh-btn"
              variant="warning" 
              size="sm" 
              v-on:click="getProfileData">
              <i class="fa fa-recycle"></i>
              <!-- Refresh -->
            </b-button>
            <b-button 
            v-else 
            data-cy="ota-profile-search-btn"
            size="lg"
            variant="success" 
            title="Search" 
            :disabled="searchDisabled"
            v-on:click="getProfileData">
            <i class="fa fa-search"></i>
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <b-form-invalid-feedback for="iccid" :state="!searchDisabled" class="text-center">
        Please enter a valid iccid.
      </b-form-invalid-feedback>
					</b-col>
					<b-col xl="6"></b-col>
				</b-row>

				<b-row v-if="submitLoading">
					<b-col xl="12">
						<b-card no-body>
							<b-card-body class="text-center">
                Loading OTA Profile... <br>
                <b-icon icon="three-dots" animation="cylon" font-scale="4"/>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>

				<b-row v-else-if="!hasData">
					<b-col xl="12">
						<b-card data-cy="ota-profile-no-results" no-body>
							<b-card-body>
								<div class="text-center">Please enter a valid iccid that exists in the system.</div>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>

				<span v-else>
					<b-row>
						<b-col xl="6">
							<b-card no-body>
								<b-card-body>
									<table class="table b-table">
										<tr>
											<th colspan="2" class="primary-table-header">
												<div class="">Subscription Information</div>
											</th>
											<th colspan="2" class="primary-table-header">
												<b-button
                          data-cy="ota-profile-full-response-btn"
                          class="float-right"
                          size="sm"
                          variant="warning"
                          v-b-tooltip.hover
                          title="View Full Response"
                          @click="$bvModal.show('full-response')"
                        >
                          <b-icon icon="info-circle"></b-icon>
                        </b-button>
											</th>
										</tr>
									</table>
									<div class="table-responsive">
										<b-table
											id="log_table"
											:items="sub_data"
											:fields="sub_fields"
                      :per-page="perPage"
                      :current-page="currentPage"
											head-row-variant="light"
											head-variant="light"
											table-class="text-nowrap"
											class="data-management-table second-table"
											sticky-header="true"
											thead-class="white"
											show-empty
											responsive
											small
										>
											<template #empty="scope">
												<div v-if="submitLoading" class="text-center">
													<b-icon icon="three-dots" animation="cylon" font-scale="4" />
												</div>
												<div v-else class="text-center">
													<b>{{ scope.emptyText }}</b>
												</div>
											</template>
											<template #cell(data_name)="row">
												{{ row.item.data_name }}
											</template>
											<template #cell(data_value)="row">
												<span
													v-if="row.item.data_value.name"
													v-b-tooltip.hover
													:title="'api_name: ' + row.item.data_value.name"
												>
													{{ row.item.data_value.display_name }}
													<b-badge variant="warning" v-if="row.item.data_value.default"
														>Default</b-badge
													>
												</span>
												<span v-else>
													{{ row.item.data_value }}
												</span>
											</template>
										</b-table>
                    <!-- <b-row v-if="!submitLoading" class="justify-content-center mb-2">
                      <b-col class="d-flex align-items-center h-100" xs="12" sm="6">
                        <b-button
                          data-cy="ota-profile-full-response-btn"
                          class="w-100"
                          variant="primary"
                          size="sm"
                          v-on:click="$bvModal.show('full-response')"
                        >
                          <i class="fa fa-info-circle mr-1"></i>
                          View Full Response
                        </b-button>
                      </b-col>
                    </b-row> -->
                    <b-row v-if="sub_data.length > perPage">
                      <b-col>
                        <p class="ml-3 nowrap">{{ startPage(perPage) }} - {{ lastPage(perPage, totalRows) }} of {{ totalRows }}</p>
                      </b-col>
                      <b-col v-if="totalRows > perPage">
                        <b-pagination
                            class="flex-fill-custom pr-4 ml-3 justify-content-end"
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            size="sm"
                        ></b-pagination>
                      </b-col>
                    </b-row>
									</div>
								</b-card-body>
							</b-card>
						</b-col>
						<b-col xl="6">
							<b-card no-body>
								<b-card-body>
									<table class="table b-table">
										<tr>
											<th colspan="2" class="primary-table-header">
												<div class="">HTTP OTA Information</div>
											</th>
										</tr>
									</table>
									<div class="table-responsive">
										<b-table
											id="log_table"
											:items="http_data"
											:fields="keyval_fields"
											head-row-variant="light"
											head-variant="light"
											table-class="text-nowrap"
											class="data-management-table second-table"
											sticky-header="true"
											thead-class="white"
											show-empty
											responsive
											small
										>
											<template #empty="scope">
												<div v-if="submitLoading" class="text-center">
													<b-icon icon="three-dots" animation="cylon" font-scale="4" />
												</div>
												<div v-else class="text-center">
													<b>{{ scope.emptyText }}</b>
												</div>
											</template>
										</b-table>
									</div>
								</b-card-body>
							</b-card>
						</b-col>
					</b-row>

					<b-row v-if="isPortalAdmin" data-cy="ota-profile-keys-table">
						<b-col xl="12">
							<b-card no-body>
								<b-card-body>
									<table class="table b-table">
										<tr>
											<th colspan="2" class="ps">
												<div>
													SIM keys &nbsp;
													<b-badge pill variant="danger"
														>THIS SECTION IS ONLY VISIBLE TO DCC PORTAL ADMIN</b-badge
													>
												</div>
											</th>
										</tr>
									</table>
									<div class="table-responsive">
										<b-table
											id="key_table"
											:items="key_data"
											:fields="keyval_fields"
											head-row-variant="light"
											head-variant="light"
											table-class="text-nowrap"
											class="data-management-table second-table"
											sticky-header="true"
											thead-class="white"
											show-empty
											responsive
											small
										>
                    <template #empty="scope">
                      <div v-if="logsLoading" class="text-center">
                        <b-icon icon="three-dots" animation="cylon" font-scale="4"/>
                      </div>
                      <div v-else class="text-center">
                        <b>{{ scope.emptyText }}</b>
                      </div>
                    </template>
										</b-table>
									</div>
								</b-card-body>
							</b-card>
						</b-col>
					</b-row>

					<b-row>
						<b-col xl="12">
							<b-card no-body>
								<b-card-body>
									<!-- LOG ITEMS -->
									<table class="table b-table">
										<tr>
											<th colspan="2" class="primary-table-header">
												<div class="">OTA Records</div>
											</th>
                      <th colspan="2" class="primary-table-header d-flex align-items-center justify-content-end">
                        <label for="logs-per-page" class="mr-2">
                          Per Page
                        </label>
                        <b-form-select
                          id="logs-per-page"
                          v-model="logsPerPage"
                          :options="[10, 25, 50, 100]"
                          class="w-auto mr-4"
                          size="sm"
                        ></b-form-select>
                      </th>
                      <!-- <th  colspan="2" class="primary-table-header">
                        <label for="logs-sort-by" class="mr-2">
                          Period
                        </label>
                        <b-form-select
                          id="logs-sort-by"
                          v-model="logsPeriod"
                          :options="logsPeriodOptions"
                          class="w-auto"
                          size="sm"
                        ></b-form-select>
                      </th> -->
										</tr>
									</table>
									<div class="">
										<b-table
                      data-cy="ota-profile-logs-table"
											id="log_table"
											:items="log_data"
											:fields="log_items_fields"
                      :per-page="logsPerPage"
                      :current-page="currentPage_logs"
											head-row-variant="light"
											head-variant="light"
											table-class="text-nowrap"
											class="data-management-table second-table"
											sticky-header="true"
											thead-class="white"
											show-empty
											responsive
											small
                      @sort-changed="onSortChanged($event)"
										>
											<template #empty="scope">
												<div v-if="submitLoading" class="text-center">
													<b-icon icon="three-dots" animation="cylon" font-scale="4" />
												</div>
												<div v-else class="text-center">
													<b>{{ scope.emptyText }}</b>
												</div>
											</template>
											<template #cell(date)="row">
												<span class="nowrap" :data-cy="`ota-profile-log-${String(row.item.date).toLowerCase()}`">
													{{ row.item.date }}
												</span>
											</template>
											<template #cell(ota)="row">
												<base-button
													size="sm"
													:type="formatSuccess(row.item.ota)"
													class="nowrap"
												>
													{{ row.item.ota }}
												</base-button>
											</template>
											<template #cell(smsota)="row">
												{{ row.item.smsota }}
											</template>
											<template #cell(username)="row">
												{{ row.item.username }}
											</template>
											<template #cell(items)="row">
												<base-button
													@click="row.toggleDetails()"
													type="primary"
													size="sm"
													style="width: 100px"
													class="nowrap"
													>Details
												</base-button>
											</template>
											<template v-slot:row-details="row">
												<table class="under-table w-auto">
													<tbody>
                            <template v-for="(value, key) in row.item">
                              <template v-if="key === 'error_status'">
                                <tr>
                                    <td colspan="2" class="error_status bg-light-form">
                                      <b>Error Details</b>
                                    </td>
                                </tr>
                                <tr class="bg-light-form">
                                    <td>
                                      From File
                                    </td>
                                    <td>
                                      {{ value.file.toString().toUpperCase() }}
                                    </td>
                                </tr>
                                <tr class="bg-light-form">
                                    <td>
                                      Error
                                    </td>
                                    <td>
                                      {{ value.info }}
                                    </td>
                                </tr>
                              </template>
                              <tr :key="key" v-if="key !== 'error_status'">
                                <td
                                  v-if="
                                    key !== 'date' &&
                                    key !== '_showDetails' &&
                                    key !== 'username' &&
                                    key !== 'items' &&
                                    key !== 'smsota' &&
                                    key !== 'error_status' &&
                                    key !== 'ota'
                                  "
                                >
                                  <b>{{ key }}</b>
                                </td>
                                <td
                                  v-if="
                                    key !== 'date' &&
                                    key !== '_showDetails' &&
                                    key !== 'username' &&
                                    key !== 'items' &&
                                    key !== 'smsota' &&
                                    key !== 'error_status' &&
                                    key !== 'ota'
                                  "
                                >
                                  {{ formatObjectData(value) }}
                                </td>
                              </tr>
                            </template>
														<tr v-for="(value, key) in row.item.items" :key="key">
															<td>
																<b v-if="isNaN(key)">{{ key }}</b>
															</td>
															<td>
																<span v-if="value.name">
																	{{ value.data }}
																	<b-badge
																		variant="warning"
																		v-if="value.name === 'default'"
																		>default</b-badge
																	>
																</span>
																<span v-else>
																	{{ value }}
																</span>
															</td>
														</tr>
													</tbody>
												</table>
											</template>
										</b-table>
                    <b-row v-if="log_data.length > logsPerPage">
                      <b-col>
                        <p class="ml-3 nowrap">{{ startPage(logsPerPage) }} - {{ lastPage(logsPerPage, totalLogRows) }} of {{ totalLogRows }}</p>
                      </b-col>
                      <b-col v-if="totalLogRows > logsPerPage">
                        <b-pagination
                            class="flex-fill-custom pr-4 ml-3 justify-content-end"
                            v-model="currentPage_logs"
                            :total-rows="totalLogRows"
                            :per-page="logsPerPage"
                            size="sm"
                        ></b-pagination>
                      </b-col>
                    </b-row>
									</div>
								</b-card-body>
							</b-card>
						</b-col>
					</b-row>
				</span>
			</div>
		</base-header>

		<!-- CONFIRM RESET -->
		<b-modal id="confirm-reset" title="Confirm Reset" hide-footer centered>
			<div class="mt-3 mb-3">
				<p class="text-center">
					Are you sure you want to reset the profile for ICCID: <strong class="font-weight-600">{{ iccid }}</strong>?
				</p>
			</div>
			<div class="mt-3 mb-3 float-right">
				<base-button type="secondary" v-on:click="$bvModal.hide('confirm-reset')">Cancel</base-button>
				&nbsp;
				<base-button type="danger" v-on:click="clickReset">Confirm Reset</base-button>
			</div>
		</b-modal>

    <!-- FULL RESPONSE -->
    <b-modal
      data-cy="ota-profile-full-response-modal" 
      id="full-response" 
      ok-only 
      centered
    >
      <b-tabs content-class="mt-3">
        <b-tab title="FIELDS" active>
          <div class="data-management-table primary-table b-table-sticky-header table-responsive">
              <table class="table b-table text-nowrap table-striped table-hover table-sm">
                <thead class="thead-light white">
                <th>Key</th>
                <th>Value</th>
                </thead>
              <tbody v-for="(value, index) in full_sub_data" :key="index">
                <tr v-if="(typeof value === 'object')" class="b-table">
                  <td class="bg-light">{{ String(index).toUpperCase() }}</td>
                  <td>
                    <tr v-for="(sub_val, sub_key) in full_sub_data[index]">
                      <td class="w-25">
                        {{ String(sub_key).toUpperCase() }}
                      </td>
                      <td class="w-50">
                        {{ sub_val }} 
                      </td>
                    </tr>
                  </td>
                </tr>
                <tr v-else class="b-table">
                  <td class="bg-light">{{ String(index).toUpperCase() }}</td>
                  <td>{{ value }}</td>
                </tr>
              </tbody>
              </table>

          </div>
        </b-tab>
        <b-tab title="RAW">
          <span class="responsive-table">
            <div v-highlight class="overflow-auto w-auto h-auto">
              <pre><code class="json">{{
                printJson(full_sub_data)
              }}</code></pre>
            </div>
          </span>
        </b-tab>
      </b-tabs>
    </b-modal>

		<!--    <b-modal id="fileInfoModel" v-model="showFileInfoModal" title="Message" centered>-->
		<!--      <div class="input-group" v-if="fileInfo">-->
		<!--        <b-input-group>-->
		<!--          <template #prepend>-->
		<!--            <b-card-body>-->
		<!--              <div>-->
		<!--                <Strong>API Name:</Strong>-->
		<!--                <span class="ml-1">{{ fileInfo.name || {} }}</span>-->
		<!--              </div>-->
		<!--              <div>-->
		<!--                <Strong>Size:</Strong>-->
		<!--                <span class="ml-1">{{ fileInfo.size || 0 }} B</span>-->
		<!--              </div>-->
		<!--              <div>-->
		<!--                <Strong>Modified:</Strong>-->
		<!--                <span class="ml-1">{{ fileInfo.date.modified || {} }}</span>-->
		<!--              </div>-->
		<!--              <div>-->
		<!--                <Strong>Disabled:</Strong>-->
		<!--                <span class="ml-1">{{ fileInfo.disabled === 1 ? "Yes" : "No" }}</span>-->
		<!--              </div>-->
		<!--            </b-card-body>-->
		<!--          </template>-->
		<!--        </b-input-group>-->
		<!--      </div>-->
		<!--    </b-modal>-->
	</div>
</template>

<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import otaDashboardService from "@/common/api-services/ota-dashboard-service";
import initService from "@/common/api-services/init-service";
import ToastVue from "../../Components/Toast.vue";
import coreutils from "@/common/coreutils";
import pagination from "../../../common/format/pagination";

export default {
	components: {
		RouteBreadCrumb,
		ToastVue,
	},

	data() {
		return {
			showFileInfoModal: false,
			fileInfo: [],
			admin_style: "background-color:rgba(255,87,51);color:#FFFFFF;width:150px",
			submitLoading: false,
      logsLoading: false,
			hasData: false,
			iccid: "",
			prevIccid: "",
      carrier: "",
      carrierFiles: {},

			//profile list
			profile_data_src: [],
			http_data: [],
			sub_data: [],
      perPage: 10,
      currentPage: 1,
			key_data: [],
			log_data: [],
      currentPage_logs: 1,
      logsPerPage: 10,
      logsPeriod: "30 Days",
      logsPeriodOptions: ["30 Days", "60 Days", "90 Days", "All"],


      full_sub_data: {
        carrier: "",
        iccid: "",
        msisdn: "",
      },

			sub_fields: [
				{key: "data_name", label: "Template"},
				{key: "data_value", label: "Data"},
				//{key: 'data_type', label: 'Type'}
			],

			keyval_fields: [
				{key: "data_name", label: "Name"},
				{key: "data_value", label: "Value"},
			],

			//https://stackoverflow.com/questions/67354006/b-table-items-for-deeply-nested-arrays
			log_items_fields: [
				{key: "date", sortable: true},
				{key: "username", sortable: true},
				{key: "ota", label: "action", sortable: true},
				//{key: 'smsota'},
				{key: "items", label: "details"},
			],
      sortBy: 'date',
      sortDirection: 'desc',

			//ACCESS ROLES
			isReadOnly: true,
			isEditor: false,
			isAdministrator: false,
			isPortalAdmin: false,
			progressTitle: null,
			progressMessage: null,
		};
	},

	mounted() {
		document.title = "OTA - Profile";

		//set the roles [isReadOnly, isEditor, isAdministrator, isPortalAdmin]
		this.isReadOnly = initService.getUserRoles().includes("OTA_READ");
		this.isEditor = initService.getUserRoles().includes("OTA_EDIT");
		this.isAdministrator =
			initService.getUserRoles().includes("CARRIER_ADMIN") ||
			initService.getUserRoles().includes("PORTAL_ADMIN") ||
			initService.getUserRoles().includes("OTA_ADMIN");
		this.isPortalAdmin = initService.getPortalAdminRole();

		//write to console.
		// console.log('PAGE:', document.title)
		// console.log('isReadOnly:', this.isReadOnly);
		// console.log('isEditor:', this.isEditor);
		// console.log('isAdministrator:', this.isAdministrator);
		// console.log('isPortalAdmin:', this.isPortalAdmin);

		//don't do anything unless the user has access
		if (!this.noAccess) {
			if (this.$route.query.iccid !== "" && typeof this.$route.query.iccid !== "undefined") {
				this.iccid = this.$route.query.iccid;
			} else {
				this.readCache();
			}
			//load page
			this.loadPageData();
		} else {
			this.$router.push("/restricted").catch(() => {});
		}

	},
	computed: {
		noAccess() {
			// Determine access based on menu link configuration
			// console.log(this.$route.path.split(':')[0])
			// console.log(this.$route.query)
			let userRoles = initService.getUserRoles();
			let link = this.$store.getters["getLinkByLink"](this.$route.path.split(":")[0] + ":id");

			if (!link) {
				// console.log('no link found')
				return true;
			} else {
				// console.log('link found', link)
				userRoles = userRoles.filter(role => link.roles.includes(role.toUpperCase()));
				return userRoles.length === 0;
			}
		},
		showRefresh() {
			if (!this.iccid || this.iccid.length < 1) {
				return false;
			}
			return this.iccid === this.prevIccid;
		},
    totalRows() {
      return this.sub_data.length;
    },
    totalLogRows() {
      return this.log_data.length;
    },
    searchDisabled() {
      // return this.iccid.length < 19 || this.iccid.length > 20;
      return this.iccid.length !== 20;
    },
	},
	methods: {
		readCache() {
			//get the last saved filter criteria
			let search = localStorage.getItem(this.$route.name);
			// console.log('reading search:', search);
			if (search) {
				search = JSON.parse(search);
				if (search.iccid && search.iccid !== "") this.iccid = search.iccid;
			}
		},

		writeCache() {
			//set to current filter criteria
			let search = {
				iccid: this.iccid,
			};
			// console.log('writing search:', search);
			localStorage.setItem(this.$route.name, JSON.stringify(search));
		},

    onSortChanged(sortInfo) {
      if (!this.log_data) {
        return;
      }
      const { sortBy, sortDesc } = sortInfo;
      // console.log("sortInfo", sortBy);
      this.sortBy = sortBy;
      this.sortDirection = sortDesc ? 'desc' : 'asc';
      let new_log_data = this.log_data.sort((a, b) => {
        // console.log(a[sortBy]);
        let comparision_a = a[sortBy];
        let comparision_b = b[sortBy];
        if (sortBy === "date") {
          comparision_a = new Date(a[sortBy]).getTime();
          comparision_b = new Date(b[sortBy]).getTime();
        }
        
        // console.log("comparision_a", comparision_a);
        // console.log("comparision_b", comparision_b);

        if (comparision_a === comparision_b) {
          return 0;
        }
        if (sortInfo.sortDesc) {
          return comparision_a < comparision_b ? 1 : -1;
        } else {
          return comparision_a > comparision_b ? 1 : -1;
        }
      });
      // console.log(this.carrierFiles);
      // console.log("new_log_data", new_log_data);
      this.log_data = new_log_data;
    },

		formatSuccess(item) {
			item = `${item}`;
			if (item.toLowerCase().includes("success") || item.toLowerCase().includes("activated")) {
				return "success";
			} else if (item.toLowerCase().includes("provisioning completed")) {
				return "danger";
			} else if (item.toLowerCase().includes("did not complete") || item.toLowerCase().includes("error")) {
				return "danger";
			} else {
				return "warning";
			}
		},

		// getFileInfo(data) {
		// 	//getInfo_API({carrier: carrier.name, name: val.name, type: val.type,});

		// 	otaDashboardService
		// 	.getCarrierFileInfo(data)
		// 	.then(res => {
		// 	  this.showFileInfoModal = true;
		// 	  this.fileInfo = res.data.result;
		// 	})
		// 	.catch(err => {
		// 	  throw err;
		// 	});
		// 	this.fileInfo = null;
		// },

		// flattenValue(obj) {
		// 	if (typeof obj === "object" && !Array.isArray(obj) && obj !== null) {
		// 		let val = "";
		// 		Object.keys(obj).forEach(item => {
		// 			val += item + ":" + obj[item] + ", ";
		// 		});
		// 		return val;
		// 	} else {
		// 		return obj;
		// 	}
		// },

		async loadPageData() {
			try {
				if (!this.iccid || this.iccid.length !== 20) {
					throw new Error("Invalid ICCID.");
				} else {
					this.getProfileData();
          this.onSortChanged({sortBy: "date", sortDesc: this.sortDirection === 'desc'});
				}
			} catch (e) {
				//this.showToasterMsg('[loadPage]Error:' + e.message, true, 'danger');
			}
		},

		clearData() {
			this.hasData = false;
			this.sub_data = null;
			this.http_data = null;
			this.key_data = null;
			this.log_data = null;
			this.profile_data_src = null;
      this.carrierFiles = null;
      this.full_sub_data = {
        carrier: "",
        iccid: "",
        msisdn: "",
      };
      this.currentPage = 1;
      this.currentPage_logs = 1;
      this.carrier = "";
		},

		async getProfileData() {
			this.submitLoading = true;
			this.clearData()
			this.iccid = this.iccid.substring(0, 20);
			this.prevIccid = this.iccid;
			this.writeCache();

      this.sub_data = [];
      this.key_data = [];
      this.http_data = [];

      await otaDashboardService
				.query_minimal(this.iccid, 0, 0)
				.then(res => {
					// console.log("QUERY_MINIMAL DATA: ", res.data)
					if (res.data.status) {
						if (res.data.result) {
							let http_data = [];
							let sub_data = [];
              let full_sub_data = {};
							let key_data = [];
              let carrier_files = []

							if (res.data.result.carrier.display_name) {
                this.carrier = res.data.result.carrier.display_name;
								// sub_data = [{data_name: "Carrier", data_value: res.data.result.carrier.display_name}];
                full_sub_data["carrier"] = res.data.result.carrier.display_name;
              }
              // carrier_files = res.data.result.carrier.files
              // this.carrierFiles = carrier_files;

							//keys table
							if (res.data.result.items.keys && this.isPortalAdmin) {
								Object.keys(res.data.result.items.keys).forEach(async function (data_key) {
									let data_val = res.data.result.items.keys[data_key];
									if (data_key === "tls") {
										data_key = "Tls Key";
									} else if (data_key === "kic") {
										data_key = "Kic Key";
									} else if (data_key === "kid") {
										data_key = "Kid Key";
									} else if (data_key === "vendor") {
										await otaDashboardService.getVendors().then(res => {
                      // console.log("VENDOR RES: ", res.data);
											if (res.data.status) {
												if (res.data.result) {
													let vendor = res.data.result.filter(vendor => vendor.name === data_val);
													if (vendor.length > 0) {
														data_val = vendor[0].display_name;
                            // console.log("VENDOR: ", data_val);
													}
												}
											}
										})
										.catch(err => {
											console.error(err);
										});
                    data_key = "Vendor";
                    // key_data.push({data_name: data_key, data_value: data_val});
									}
                  key_data.push({data_name: data_key, data_value: data_val});
								});
                // console.log("KEY DATA: ", key_data);
                if (key_data.length > 0) {
                  this.key_data = key_data;
                }
							}

							//http table
              if (res.data.result.items.data != null) {
                Object.keys(res.data.result.items.data).forEach(function (data_key) {
                  let data_val = "";
                  let data_type = "field";
                  if (data_key === "state") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "Provision State";
                    //0=complete, 1=provision, 2=testing, 3= PRL only, 4=Downloading.
                    switch (data_val) {
                      case "0":
                        data_val = "Complete(0)";
                        break;
                      case "1":
                        data_val = "Waiting(1)";
                        break;
                      case "2":
                        data_val = "Testing(2)";
                        break;
                      case "3":
                        data_val = "PRL only(3)";
                        break;
                      case "4":
                        data_val = "Downloading(4)";
                        break;
                      default:
                        data_val = "Active";
                        break;
                    }
                  } else if (data_key === "lastotatime") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "Last SIM Provision";
                    if (data_val === "") data_val = "Never";
                  } else if (data_key === "lastqueriedtime") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "Last SIM Checkin";
                    if (data_val === "") data_val = "Never";
                  } else if (data_key === "lastupdatetime") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "Last Record Updated";
                    if (data_val === "") data_val = "Never";
                  } else if (data_key === "httppoll") {
                    data_val = res.data.result.items.data[data_key];
                    if (data_val.hasOwnProperty("data")) {
                      data_val = data_val.data;
                    }
                    // if (data_val === "120") data_val = "2 Minutes";
                    let minutes = parseInt(data_val) / 60;
                    if (minutes === 1) {
                      data_val = "1 Minute";
                    } else if (minutes === 60) {
                      data_val = "1" + " Hour";
                    } else if (minutes > 60) {
                      // calculate hours and minutes
                      let hours = Math.floor(minutes / 60);
                      minutes = minutes % 60;
                      if (hours === 1) {
                        data_val = hours + " Hour";
                      } else {
                        data_val = hours + " Hours";
                      }
                      if (minutes === 1) {
                        data_val += " 1 Minute";
                      } else if (minutes > 1) {
                        data_val += " " + minutes + " Minutes";
                      }
                    } else {
                      data_val = minutes + " Minutes";
                    }
                    data_key = "HTTP Poll Interval";
                  } else if (data_key === "activated") {
                    data_val = res.data.result.items.data[data_key] ? "Activated" : "Not Activated";
                    data_key = "SIM Status";
                  } else if (data_key === "disabled") {
                    data_val = res.data.result.items.data[data_key]
                      ? "SIM Disabled"
                      : "SIM Not Disabled";
                    data_key = "Disabled Status";
                  } else if (data_key === "transferred") {
                    data_val = res.data.result.items.data[data_key]
                      ? "OTA transferred"
                      : "Not transferred";
                    data_key = "Transferred";
                  } else if (data_key === "transferred_iccid") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "Transferred ICCID";
                    http_data.push({data_name: data_key, data_value: data_val});
                  }
  
                  //store key
                  if (data_key !== "" && data_val !== "") {
                    data_val = data_val.toString().replace("default", "Default");
                    http_data.push({data_name: data_key, data_value: data_val});
                    data_val = "";
                  }
  
                  //Subscription Information table
                  if (data_key === "iccid") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "ICCID";
                  } else if (data_key === "carrier") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "Carrier";
                  } else if (data_key === "mdn") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "MDN";
                  } else if (data_key === "min") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "MIN";
                  } else if (data_key === "msisdn") {
                    data_val = res.data.result.items.data[data_key];
                    data_key = "MSISDN";
                  } else if (data_key === "sipupp") {
                    data_val = res.data.result.items.data[data_key]["data"];
                    data_key = "EF_SIPUPP";
                  } else if (data_key === "mipupp") {
                    data_val = res.data.result.items.data[data_key]["data"];
                    data_key = "EF_MIPUPP";
                  } else if (data_key === "hrpdupp") {
                    data_val = res.data.result.items.data[data_key]["data"];
                    data_key = "EF_HRPDUPP";
                  } else if (data_key === "eprl") {
                    data_type = "file";
                    data_val = res.data.result.carrier.files[data_key].filter(
                      element => element.name === res.data.result.items.data[data_key]
                    )[0];
                    data_key = "ePRL";
                  } else if (data_key === "oplmn") {
                    data_type = "file";
                    data_val = res.data.result.carrier.files[data_key].filter(
                      element => element.name === res.data.result.items.data[data_key]
                    )[0];
                    data_key = "OPLMN";
                  } else if (data_key === "smsp") {
                    data_type = "file";
                    data_val = res.data.result.carrier.files[data_key].filter(
                      element => element.name === res.data.result.items.data[data_key]
                    )[0];
                    data_key = "SMSP";
                  } else if (data_key === "mbi") {
                    data_type = "file";
                    data_val = res.data.result.carrier.files[data_key].filter(
                      element => element.name === res.data.result.items.data[data_key]
                    )[0];
                    data_key = "MBI";
                  } else if (data_key === "mbdn") {
                    data_type = "file";
                    data_val = res.data.result.carrier.files[data_key].filter(
                      element => element.name === res.data.result.items.data[data_key]
                    )[0];
                    data_key = "MBDN";
                  }
  
                  //console.log({'data_name': data_key, 'data_value': data_val, 'data_type': data_type});
                  //store key
                  if (data_key !== "" && data_val !== "") {
                    sub_data.push({data_name: data_key, data_value: data_val, data_type: data_type});
                    full_sub_data[data_key.toLowerCase()] = data_val;
                  }
                });
              }

							this.sub_data = sub_data;
              this.full_sub_data = full_sub_data;
							this.http_data = http_data;
							this.key_data = key_data;
							this.log_data = res.data.result.log_items;
							this.profile_data_src = res.data.result;
						}
					} else {
						console.error("Error from ELSE:", res.data.error_string);

						let errorString = res.data.error_string ? 
							res.data.error_string 
							: 
							"Query Failed - Contact suppport if the problem persists.";

						this.$toast.error({
							component: ToastVue,
							props: {
								subtitle: "ERROR",
								description: errorString,
							},
						});
					}
				})
				.catch(err => {
					console.error(err);
				})

      // Then get most recent data from OTA server
      let omitted = ["status","errorcode","info","smscounter","masterkey","lastupdatetime","inserttime"];

      let sub_order = ["carrier","iccid","imsi","isimimsi","msisdn","mdn","min","mcc"];
      let http_order = ["http poll interval","sim status","provisioning state","last sim checkin","last record updated","last sim provision"]

      await otaDashboardService.getProfile(this.iccid)
        .then((res) => {
          // console.log("PROFILE RES: ", res.data);
          if (res.data.result) {
            if (typeof res.data.result === "object") {
              this.sub_data = [];
              this.http_data = [];
              for (const key in res.data.result) {
                let data_val = res.data.result[key];
                // console.log("KEY: ", key);
                if (omitted.includes(key)) {
                  continue;
                }
                if (key === "provisioningstate") {
									//0=complete, 1=provision, 2=testing, 3= PRL only, 4=Downloading.
									switch (data_val) {
										case "0":
											data_val = "Complete(0)";
											break;
										case "1":
											data_val = "Waiting(1)";
											break;
										case "2":
											data_val = "Testing(2)";
											break;
										case "3":
											data_val = "PRL only(3)";
											break;
										case "4":
											data_val = "Downloading(4)";
											break;
										default:
											data_val = "Active";
											break;
									}
                  this.http_data.push({data_name: "Provisioning State", data_value: data_val});
                } 
                else if (key === "httppoll") {
                  data_val = this.formatHttpPoll(data_val);
                  this.http_data.push({data_name: "HTTP Poll Interval", data_value: data_val});
                }
                else if (key === "lastotatime") {
                  this.http_data.push({data_name: "Last SIM Provision", data_value: data_val});
                }
                else if (key === "lastqueriedtime") {
                  this.http_data.push({data_name: "Last SIM Checkin", data_value: data_val});
                }
                else if (key === "lastupdatetime") {
                  this.http_data.push({data_name: "Last Record Updated", data_value: data_val});
                }
                else {
                  // Subscription Information table
                  if (data_val !== "none" && data_val !== "not-set" && data_val !== "") {
                    if (key === "inserttime") {
                      this.sub_data.push({data_name: "Insert Time", data_value: data_val});
                    } else if (key === "carrier") {
                      if (this.carrier !== "") {
                        this.sub_data.push({data_name: "Carrier", data_value: this.carrier});
                      } else {
                        this.sub_data.push({data_name: "Carrier", data_value: data_val});
                      }
                    } else {
                      this.sub_data.push({data_name: key.toUpperCase(), data_value: data_val});
                    }
                  }
                  this.full_sub_data[key] = data_val;
                }
              }
            }
          } else {
            console.error("No data found")
            this.hasData = false;
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          if (this.http_data.length > 0) {
            this.http_data = this.sortTableData(this.http_data, http_order);
            this.hasData = true;
          }

          if (this.sub_data.length > 0) {
            let hasCarrier = false;
            this.sub_data.forEach((item) => {
              if (item.data_name.toLowerCase() === "carrier") {
                hasCarrier = true;
                return;
              }
            });
            if (!hasCarrier) {
              this.sub_data.push({data_name: "Carrier", data_value: this.carrier});
            }
            this.sub_data = this.sortTableData(this.sub_data, sub_order);
            this.hasData = true;
          }
          
          if (this.hasData) {
            this.getProfileLogs();
          }
          this.submitLoading = false;
        });

		},

    sortTableData(array, template) {
      array.sort((a, b) => {
        if (template.includes(a.data_name.toLowerCase()) && template.includes(b.data_name.toLowerCase())) {
          return template.indexOf(a.data_name.toLowerCase()) - template.indexOf(b.data_name.toLowerCase());
        } else if (template.includes(a.data_name.toLowerCase())) {
          return -1;
        } else if (template.includes(b.data_name.toLowerCase())) {
          return 1;
        }
      });
      return array;
    },

    formatHttpPoll(interval) {
      // console.log("HTTP POLL: ", interval);
      let res = "";
      let minutes = parseInt(interval) / 60;
      minutes = Math.floor(minutes);
      let hours;
      let days;
      let weeks;
      if (minutes === 1) {
        res = "1 Minute";
      } else if (minutes === 60) {
        res = "1" + " Hour";
      } else if (minutes > 60) {
        // calculate hours and minutes
        hours = Math.floor(minutes / 60);
        days = Math.floor(hours / 24);
        minutes = minutes % 60;
        if (hours === 1) {
          res = hours + " Hour";
        } else if (days >= 1) {
          if (days >= 7) {
            weeks = Math.floor(days / 7);
            days = days % 7;
            if (weeks === 1) {
              res = weeks + " Week";
            } else {
              res = weeks + " Weeks";
            }
          } else if (days === 1) {
            res = days + " Day";
          } else {
            res = days + " Days";
          }
          if (Math.floor(hours % 24) === 1) {
            res += " 1 Hour";
          } else if (Math.floor(hours % 24) > 1) {
            res += " " + Math.floor(hours % 24) + " Hours";
          }
        } else {
          res = hours + " Hours";
        }
        if (minutes === 1) {
          res += " 1 Minute";
        } else if (minutes > 1) {
          res += " " + minutes + " Minutes";
        }
      } else {
        res = minutes + " Minutes";
      }
      // console.table({minutes: minutes, hours: hours, days: days, res: res})
      return res;
    },

    async getProfileLogs() {
      this.log_data = [];
      this.logsLoading = true;
      await otaDashboardService.getProfileLogs(this.iccid)
      .then((res) => {
        // console.log("LOGS RES: ", res.data);
        if (res.data.status && res.data.result.hasOwnProperty("logs")) {
          if (res.data.result.logs.length > 0) {
            this.log_data = res.data.result.logs.map((item) => {
              if (item.hasOwnProperty("ota")) {
                item.ota = item.ota ? "Activated OTA" : "";
              }
  
              if (item.hasOwnProperty("smsota") && item.smsota) {
                item.smsota = item.smsota ? "Activated OTA (SMS)" : "";
                item.ota = "Activated OTA (SMS)";
              }
  
              if (item.hasOwnProperty("otareset")) {
                item.ota = "Reset defaults";
              }
  
              if (!item.hasOwnProperty("date") && item.hasOwnProperty("start")) {
                item.date = item.start;
                item.username = "system";
  
                if (JSON.stringify(item).toLowerCase().includes("error")) {
                  item.ota = "Provision did not complete";
                  if(item.hasOwnProperty("finished") && item.finished === 1){
                    item.ota = "Provisioning Completed";
                  }
                  if (item.hasOwnProperty("error_status")) {
                    try {
                      item.error_status = item.error_status;
                      // console.log(item.error_status)
                      item.ota = "Provisioning completed with errors";
                    } catch (error) {
                      item.error_status = {file: 'NULL', info: item.error_status}
                      console.error("error parsing error_status", error)
                    }
                  }
                  // if (item.hasOwnProperty("error_string")) {
                  //   let error;
                  //   try {
                  //     error = item.error_string;
                  //     item.error_status = {file: error.file, info: error.info}
                  //   } catch (err) {
                  //     item.error_status = {file: "ERROR", info: "Error parsing message"}
                  //   }
                  //   delete item.error_string
                  // }
                } else {
                  item.ota = "Provisioned Successfully";
                }
              }
              return item
            })
          }
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.onSortChanged({sortBy: this.sortBy, sortDesc: this.sortDirection === 'desc'});
        setTimeout(() => {
          this.logsLoading = false;
        }, 5000);
        // this.logsLoading = false;
      });
    },

		formatObjectData(str) {
			// console.log(str);
			try {
				if (typeof str === "string") {
					return str;
				} else if (typeof str === "object") {
					let arr = Object.entries(str);
					let out = "";
					let cnt = 0;
					Object.entries(str).forEach(entry => {
						cnt++;
						if (out.includes(entry[1])) out += cnt + ": " + entry[1] + ", ";
					});
					out = out.substring(0, out.length - 2); //trim last 2 char
					return out;
				} else {
					return str;
				}
			} catch (e) {
				return str;
			}
		},

		// formatStatus(str) {
		// 	str = `${str}`;
		// 	str = str.toLowerCase();
		// 	if (str === "true") {
		// 		str = "Activated";
		// 	} else if (str === "false") {
		// 		str = "Not Activated";
		// 		// } else if (String(str)) {
		// 		//   str = str.replace('{','').replace('}','');
		// 		// } else {
		// 		//str = txt;
		// 	}
		// 	return str;
		// },

    printJson(text) {
      return coreutils.formatJson(text);
    },

		refreshPage() {
			setTimeout(() => {
				this.profile_data_src = null;
				this.loadPageData();
			}, 1000);
		},

		showToasterMsg(message, append = false, variant = "success") {
			//setup for alert
			this.$toast(
				{
					component: ToastVue,
					props: {
						description: message,
					},
				},
				{
					type: variant,
				}
			);
		},

		clickActivate() {
			this.$router.push({path: `/ota/activate/:?iccid=${this.iccid}`});
		},

		clickReset() {
			this.$bvModal.hide("confirm-reset");
			this.$root.$emit("loadingPanelShow", true);
			otaDashboardService.reset(this.iccid).then(res => {
				setTimeout(() => {
					this.$root.$emit("loadingPanelHide", true);
				}, 300);
				if (res.data.status === true) {
					this.$toast.success({
						component: ToastVue,
						props: {
							description: "Successfully Reset",
						},
					});
					this.refreshPage();
				}
			});
		},

    startPage(perPage) {
      return pagination.startPageFunc(this.currentPage, perPage);
    },

    lastPage(perPage, totalRows) {
      return pagination.lastPageFunc(this.currentPage, perPage, totalRows);
    },
    
	},
};
</script>